import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { map, take, catchError } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

const POST_LOGIN_REDIRECT_KEY = 'workos_post_login_redirect';
// Earlier builds kept the WorkOS access token here. It is no longer stored: it lives only in
// the HttpOnly cookie, out of reach of scripts. The key remains so that old copies are removed.
const LEGACY_WORKOS_ACCESS_TOKEN_KEY = 'workos_access_token';
// Set while a login must wait for a click: see requireClickToLogin(). Kept in localStorage so
// that it holds in every tab, and after a reload.
const LOGIN_NEEDS_CLICK_KEY = 'workos_login_needs_click';
// When this tab last sent the browser to WorkOS without being asked
const AUTOMATIC_LOGIN_KEY = 'workos_automatic_login_at';
// Needing to log in again sooner than this means the login is not working (or the user pressed
// Back from the WorkOS page), so the next one waits for a click
const AUTOMATIC_LOGIN_INTERVAL_MS = 60 * 1000;
// Set from login until the session ends. Only the backend knows whether a session is still good,
// and it says so by answering 401: see sessionEnded(). The access token's expiry would not tell
// us, since the backend renews that token every few minutes without the front end seeing it.
const LOGGED_IN_KEY = 'logged_in';
// Earlier builds kept the access token's expiry here. The key remains so that old copies are removed.
const LEGACY_EXPIRES_AT_KEY = 'expires_at';

import { environment } from 'src/environments/environment';
import { UserService } from '../user/user.service';
import { CountryService } from '../country/country.service';
import { DocumentService } from '../document/document.service';
import { OrganizationService } from '../organization/organization.service';
import { UpdateService } from '../update/update.service';
import { EventService } from '../event/event.service';
import { ChatStateService } from '../chat-state/chat-state.service';
import { MixpanelService } from '../mixpanel/mixpanel.service';
import { storeImpersonator, storedImpersonator } from './impersonation';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiRoot = environment.api_root;

  // the desired redirect url after login
  public redirectUrl: string;

  // Logout requests that have not been answered yet, and whether one has sent the browser to
  // WorkOS to end its session. Until that page loads, this one is still running.
  private logoutsInProgress = 0;
  private leavingToLogOut = false;

  constructor(
    private http: HttpClient,
    private router: Router,
    private userService: UserService,
    private countryService: CountryService,
    private documentService: DocumentService,
    private organizationService: OrganizationService,
    private updateService: UpdateService,
    private eventService: EventService,
    private mixpanelService: MixpanelService,
    private chatState: ChatStateService
  ) {}

  // Fetches the WorkOS authorization URL from the backend and redirects the browser to it.
  // Persists redirectUrl to sessionStorage first since the in-memory value won't survive the full-page navigation.
  public getAuthorizationUrl(): Observable<void> {
    if (this.redirectUrl) {
      sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, this.redirectUrl);
    }
    return this.http
      .get<{ authorization_url: string }>(this.apiRoot.concat('headless/auth/login'))
      .pipe(
        map((response) => {
          this.redirectToExternalUrl(response.authorization_url);
        })
      );
  }

  // Whether a login may start without the user asking for it. Not when a click is required, and
  // not straight after another automatic login, which would loop if logins keep failing. Not
  // during a logout either: the session's requests fail as it ends, which brings up the login
  // page, and a login started from there would race the trip to WorkOS and could undo the logout.
  public canStartLoginAutomatically(): boolean {
    if (this.leavingToLogOut || this.logoutsInProgress > 0 || localStorage.getItem(LOGIN_NEEDS_CLICK_KEY)) {
      return false;
    }
    const startedAt = Number(sessionStorage.getItem(AUTOMATIC_LOGIN_KEY));
    return !startedAt || Date.now() - startedAt >= AUTOMATIC_LOGIN_INTERVAL_MS;
  }

  // Sends the browser to WorkOS without the user having asked. Check canStartLoginAutomatically() first.
  public startAutomaticLogin(): Observable<void> {
    sessionStorage.setItem(AUTOMATIC_LOGIN_KEY, String(Date.now()));
    return this.getAuthorizationUrl();
  }

  // Sends the browser to WorkOS because the user clicked to log in
  public startRequestedLogin(): Observable<void> {
    localStorage.removeItem(LOGIN_NEEDS_CLICK_KEY);
    return this.getAuthorizationUrl();
  }

  // The user chose to log out, but the session WorkOS holds could not be ended. A login started
  // by itself would sign them straight back in, so the next one waits for a click.
  public requireClickToLogin(): void {
    localStorage.setItem(LOGIN_NEEDS_CLICK_KEY, '1');
  }

  // Wraps window.location.href assignment so tests can spy on it.
  public redirectToExternalUrl(url: string): void {
    window.location.href = url;
  }

  // Exchanges the WorkOS callback code for a session. The backend sets the session cookie
  // (wos_session, HttpOnly), which is the wire-level credential.
  public handleCallback(code: string): Observable<boolean> {
    return this.http
      .post<{ impersonator?: { email: string } | null }>(
        this.apiRoot.concat('headless/auth/callback'),
        { code }
      )
      .pipe(
        map((response) => {
          localStorage.setItem(LOGGED_IN_KEY, '1');
          localStorage.removeItem(LEGACY_EXPIRES_AT_KEY);
          localStorage.removeItem(LOGIN_NEEDS_CLICK_KEY);
          storeImpersonator(response.impersonator?.email ?? null);

          // Support staff impersonating a user must not be recorded as that user logging in
          if (!this.impersonator) {
            this.userService
              .currentUser(false)
              .pipe(take(1))
              .subscribe((user) => {
                this.mixpanelService.init(user.email);
                this.mixpanelService.track('Login success');
              });
          }

          return this.isLoggedIn();
        })
      );
  }

  // The support person signed in as this user through WorkOS impersonation, or null.
  public get impersonator(): string | null {
    return storedImpersonator();
  }

  public consumePostLoginRedirect(): string | null {
    const stored = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    if (stored) {
      sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
      return stored;
    }
    if (this.redirectUrl) {
      const url = this.redirectUrl;
      this.redirectUrl = null;
      return url;
    }
    return null;
  }

  // Ends the WorkOS session by POSTing to the backend logout endpoint, then redirects to the
  // returned logout_url so WorkOS itself clears its session. The backend reads the workos access
  // token out of the wos_session cookie, so no request body is needed. Local state is cleared
  // before the redirect so the app session ends even if the round-trip is interrupted. On any
  // failure, falls back to client-side-only cleanup so the user isn't stuck.
  // Emits whether the browser has been sent to WorkOS; if not, the caller decides where to go.
  public logout(): Observable<boolean> {
    this.logoutsInProgress++;
    return this.http
      .post<{ logout_url: string }>(
        this.apiRoot.concat('headless/auth/logout'),
        {}
      )
      .pipe(
        map((response) => {
          this.logoutsInProgress--;
          this.leavingToLogOut = true;
          this.clearLocalSession();
          // WorkOS is about to end its session, so the next login can start by itself
          localStorage.removeItem(LOGIN_NEEDS_CLICK_KEY);
          sessionStorage.removeItem(AUTOMATIC_LOGIN_KEY);
          this.redirectToExternalUrl(response.logout_url);
          return true;
        }),
        catchError(() => {
          this.logoutsInProgress--;
          this.clearLocalSession();
          return of(false);
        })
      );
  }

  // The backend has rejected the session (a 401). Goes back through WorkOS to the same page: if
  // WorkOS still holds a session the user is signed straight back in, and otherwise they see its
  // sign-in form. Does not end that session, since nobody asked to log out. Several requests can
  // fail together, and the first one to get here is the one that acts.
  public sessionEnded(): void {
    if (this.isLoggedOut() || this.leavingToLogOut || this.logoutsInProgress > 0) {
      return;
    }
    // During a navigation the router's own url is still the page being left
    const navigation = this.router.getCurrentNavigation();
    this.redirectUrl = navigation ? navigation.extractedUrl.toString() : this.router.url;
    this.clearLocalSession();
    if (this.canStartLoginAutomatically()) {
      this.startAutomaticLogin().subscribe(
        () => { /* The browser has been sent to WorkOS */ },
        () => this.router.navigate(['login'])
      );
    } else {
      this.router.navigate(['login']);
    }
  }

  // Clears local session state without contacting the WorkOS logout endpoint.
  // Use this when the session is not being ended at WorkOS (e.g. the backend has
  // rejected it, or AuthGuard is clearing stale state). The 'token' cleanup is
  // defensive — older builds stored a JWT under that key.
  public clearLocalSession() {
    localStorage.removeItem('token');
    localStorage.removeItem(LOGGED_IN_KEY);
    localStorage.removeItem(LEGACY_EXPIRES_AT_KEY);
    localStorage.removeItem(LEGACY_WORKOS_ACCESS_TOKEN_KEY);
    storeImpersonator(null);
    this.userService.clearCache();
    this.countryService.clearCache();
    this.documentService.clearCache();
    this.organizationService.clearCache();
    this.updateService.clearCache();
    this.eventService.clearCache();
    this.chatState.clearCache();
  }

  // Asks the backend to send a password reset email to the currently logged-in user.
  // The JWT identifies the user; no body is required. The email links to WorkOS's
  // hosted reset flow.
  public sendPasswordResetEmail(): Observable<void> {
    return this.http.post<void>(
      this.apiRoot.concat('headless/auth/password-reset'),
      {}
    );
  }

  public isLoggedIn(): boolean {
    return localStorage.getItem(LOGGED_IN_KEY) !== null;
  }

  public isLoggedOut(): boolean {
    return !this.isLoggedIn();
  }

}
